import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Eyebrow, Field, Input, money, formatDateTime, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT, type StringKey } from '../i18n/index.js';

/**
 * Dispute inbox (docs/06 "/disputes"). Grouped by status so open requests are
 * the first thing on screen; a resolution that moves money is gated on the
 * refunds capability, matching the server.
 */

/**
 * Module scope, so the label cannot be translated here — `t` is a hook. The table
 * carries the KEY and the render call resolves it, which also means switching
 * language re-labels the filter without rebuilding the list.
 */
const STATUS_FILTERS: ReadonlyArray<{ value: string; labelKey: StringKey }> = [
  { value: '', labelKey: 'f.all' },
  { value: 'OPEN', labelKey: 'st.open' },
  { value: 'IN_REVIEW', labelKey: 'st.inReview' },
  { value: 'RESOLVED_REFUNDED', labelKey: 'st.refunded' },
  { value: 'CLOSED', labelKey: 'st.closed' },
];

interface DisputeRow {
  id: string;
  type: string;
  subject: string;
  status: string;
  createdAt: string;
  booking: { reference: string; contactName: string; totalCents: number; currency: string };
  _count: { messages: number };
}

interface DisputeDetail extends DisputeRow {
  resolutionNote: string | null;
  messages: Array<{ id: string; author: string; body: string; createdAt: string }>;
  booking: DisputeRow['booking'] & {
    status: string;
    refundedCents: number;
    trip: { departureAt: string; route: { name: string } };
    seats: Array<{ seatCode: string; passengerName: string }>;
    payments: Array<{ status: string; amountCents: number; refundedCents: number }>;
  };
}

export function Disputes() {
  const t = useT();
  const { tenant, user } = useSession();
  const timezone = tenant?.timezone ?? 'UTC';
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const query = new URLSearchParams({ pageSize: '100' });
  if (status) query.set('status', status);
  const disputes = useResource<DisputeRow[]>(`/admin/disputes?${query.toString()}`);

  const columns: Column<DisputeRow>[] = [
    {
      key: 'subject',
      header: t('f.request'),
      render: (row) => (
        <div>
          <p className="font-medium">{row.subject}</p>
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {row.type.replace(/_/g, ' ').toLowerCase()} · {row._count.messages} message
            {row._count.messages === 1 ? '' : 's'}
          </p>
        </div>
      ),
    },
    {
      key: 'booking',
      header: t('f.booking'),
      render: (row) => (
        <span className="tabular">
          {row.booking.reference}
          <span style={{ color: 'var(--color-faint)' }}> · {row.booking.contactName}</span>
        </span>
      ),
    },
    {
      key: 'value',
      header: t('f.value'),
      numeric: true,
      render: (row) => money(row.booking.totalCents, row.booking.currency),
    },
    {
      key: 'opened',
      header: t('f.opened'),
      render: (row) => formatDateTime(row.createdAt, timezone),
    },
    { key: 'status', header: t('f.status'), render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <Page
      title={t('nav.disputes')}
      description={t('dp.subtitle')}
      actions={
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map(({ value, labelKey }) => (
            <Button
              key={value}
              size="sm"
              variant={status === value ? 'primary' : 'secondary'}
              onClick={() => setStatus(value)}
            >
              {t(labelKey)}
            </Button>
          ))}
        </div>
      }
    >
      <DataTable
        columns={columns}
        rows={disputes.data}
        rowKey={(row) => row.id}
        loading={disputes.loading}
        error={disputes.error}
        onRowClick={(row) => setSelected(row.id)}
        empty={{
          title: status ? 'Nothing in this view' : 'No disputes',
          description: t('dp.emptyBody'),
        }}
      />

      {selected && (
        <DisputeDrawer
          disputeId={selected}
          timezone={timezone}
          canRefund={user?.capabilities.includes('refunds:issue') ?? false}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            await disputes.reload();
          }}
        />
      )}
    </Page>
  );
}

function DisputeDrawer({
  disputeId,
  timezone,
  canRefund,
  onClose,
  onChanged,
}: {
  disputeId: string;
  timezone: string;
  canRefund: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const dispute = useResource<DisputeDetail>(`/admin/disputes/${disputeId}`);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/disputes/${disputeId}/messages`, { body: reply.trim() });
      setReply('');
      await dispute.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not send your reply.'));
    } finally {
      setBusy(false);
    }
  }

  async function resolve(action: 'REVIEW' | 'REFUND' | 'REJECT' | 'CLOSE') {
    if (action === 'REFUND') {
      const total = dispute.data ? money(dispute.data.booking.totalCents, dispute.data.booking.currency) : '';
      const confirmed = await ask({
        title: `Refund ${total} to ${dispute.data?.booking.contactName}?`,
        body: 'The money goes back to their card and their seats are released. This cannot be undone here.',
        confirmLabel: 'Refund',
      });
      if (!confirmed) return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/disputes/${disputeId}/resolve`, {
        action,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setNote('');
      await dispute.reload();
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not update this request.'));
    } finally {
      setBusy(false);
    }
  }

  const data = dispute.data;
  const settled = data ? data.status.startsWith('RESOLVED') || data.status === 'CLOSED' : false;

  return (
    <Drawer open title={t('f.request')} onClose={onClose}>
      {dispute.loading && <p style={{ color: 'var(--color-muted)' }}>Loading…</p>}
      {dispute.error && <Alert tone="error">{dispute.error}</Alert>}
      {data && (
        <div className="flex flex-col gap-5">
          <div>
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold">{data.subject}</h3>
              <StatusBadge status={data.status} />
            </div>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
              Opened {formatDateTime(data.createdAt, timezone)}
            </p>
          </div>

          <Card>
            <Eyebrow>{t('f.booking')}</Eyebrow>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.reference')}</dt>
                <dd className="tabular font-medium">{data.booking.reference}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.passenger')}</dt>
                <dd className="font-medium">{data.booking.contactName}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.trip')}</dt>
                <dd className="font-medium">{data.booking.trip.route.name}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.departs')}</dt>
                <dd className="font-medium">
                  {formatDateTime(data.booking.trip.departureAt, timezone)}
                </dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.paid')}</dt>
                <dd className="money font-medium">
                  {money(data.booking.totalCents, data.booking.currency)}
                </dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('dp.bookingStatus')}</dt>
                <dd className="font-medium">{data.booking.status.replace(/_/g, ' ').toLowerCase()}</dd>
              </div>
            </dl>
            {data.booking.refundedCents > 0 && (
              <p className="mt-2 text-sm" style={{ color: 'var(--signal-departed)' }}>
                Refunded {money(data.booking.refundedCents, data.booking.currency)}
              </p>
            )}
          </Card>

          <div>
            <Eyebrow>{t('f.conversation')}</Eyebrow>
            <ol className="mt-2 flex flex-col gap-3">
              {data.messages.map((message) => (
                <li
                  key={message.id}
                  className="rounded-lg px-3 py-2"
                  style={{
                    background:
                      message.author === 'STAFF' ? 'var(--color-primary-wash)' : 'var(--color-surface)',
                  }}
                >
                  <p className="text-[0.6875rem] uppercase tracking-wider" style={{ color: 'var(--color-faint)' }}>
                    {message.author === 'STAFF' ? 'You' : 'Passenger'} ·{' '}
                    {formatDateTime(message.createdAt, timezone)}
                  </p>
                  <p className="mt-0.5 text-sm whitespace-pre-wrap">{message.body}</p>
                </li>
              ))}
            </ol>

            {data.status !== 'CLOSED' && (
              <form onSubmit={send} className="mt-3 flex gap-2">
                <Input
                  aria-label={t('dp.replyTo')}
                  placeholder={t('dp.replyTo')}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <Button type="submit" loading={busy} disabled={!reply.trim()}>
                  {t('f.reply')}
                </Button>
              </form>
            )}
          </div>

          {error && <Alert tone="error">{error}</Alert>}

          {!settled && (
            <div>
              <Eyebrow>{t('f.resolve')}</Eyebrow>
              <div className="mt-2">
                <Field label={t('dp.resolutionNote')} htmlFor="d-note" hint={t('dp.sentToPax')}>
                  <Input id="d-note" value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.status === 'OPEN' && (
                  <Button size="sm" variant="secondary" loading={busy} onClick={() => resolve('REVIEW')}>
                    {t('dp.markInReview')}
                  </Button>
                )}
                {canRefund ? (
                  <Button size="sm" loading={busy} onClick={() => resolve('REFUND')}>
                    {t('dp.approveRefund')}
                  </Button>
                ) : (
                  <span className="self-center text-xs" style={{ color: 'var(--color-muted)' }}>
                    {t('bk.noRefundRole')}
                  </span>
                )}
                <Button size="sm" variant="secondary" loading={busy} onClick={() => resolve('REJECT')}>
                  {t('f.reject')}
                </Button>
                <Button size="sm" variant="ghost" loading={busy} onClick={() => resolve('CLOSE')}>
                  {t('f.close')}
                </Button>
              </div>
            </div>
          )}

          {settled && data.resolutionNote && (
            <Alert tone="info" title={t('f.resolution')}>
              {data.resolutionNote}
            </Alert>
          )}
        </div>
      )}
      {dialog}
    </Drawer>
  );
}
